import { faAngleDown } from '@fortawesome/pro-solid-svg-icons/faAngleDown';
import { faBookmark } from '@fortawesome/pro-solid-svg-icons/faBookmark';
import { faFilterSlash } from '@fortawesome/pro-solid-svg-icons/faFilterSlash';
import { faGlobe } from '@fortawesome/pro-solid-svg-icons/faGlobe';
import { faInfinity } from '@fortawesome/pro-solid-svg-icons/faInfinity';
import { faMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons/faMagnifyingGlass';
import { useSearch } from '@tanstack/react-router';
import useDebounceEffect from 'ahooks/lib/useDebounceEffect';
import clsx from 'clsx';
import {
  type ChangeEvent,
  type ChangeEventHandler,
  type ComponentProps,
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Col, Dropdown, Form, InputGroup, Row } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';

import type { DealerBoothListElementDto } from '@furality/fox-client/models';
import { useIsRegistered } from '@furality/frontend-stack';
import { Button } from '@furality/furality-purpose';
import Booth from '@/components/_dealers/Booth/index.tsx';
import {
  BoothTab,
  BoothTabIdIncrease,
  BoothTabSection,
  BoothTabSectionIdIncrease,
} from '@/components/_dealers/BoothEnums.ts';
import { boothIdFromIndex, boothIndexFromId } from '@/components/_dealers/boothMapping.ts';
import Map from '@/components/_dealers/Map.tsx';
import TagItem from '@/components/_dealers/TagItem.tsx';
import FontAwesomeIcon from '@/components/FontAwesomeIcon.tsx';
import TooltipHelper from '@/components/TooltipHelper.tsx';
import useFlag from '@/lib/hooks/useFlag.ts';

import BoothCard from './BoothCard.tsx';
import searchbarStyle from './DealersSearchbar.module.scss';
import JoinDealersWorldButton from './JoinWorldButton.tsx';
import { sectionI18nKey, tabI18nKey } from './tabI18n.ts';

export const NO_BOOTH = -1;

const getBoothLocation = (
  boothId: DealerBoothListElementDto['boothId'],
): {
  world: BoothTab | null;
  section: BoothTabSection | null;
} => {
  const location: ReturnType<typeof getBoothLocation> = {
    world: null,
    section: null,
  };

  let calc = boothId;

  // Calculate world
  if (boothId >= 100 && boothId < 400) {
    location.world = BoothTab.WORLD1;
    calc -= BoothTabIdIncrease[BoothTab.WORLD1];
  } else if (boothId >= 400 && boothId < 700) {
    location.world = BoothTab.WORLD2;
    calc -= BoothTabIdIncrease[BoothTab.WORLD2];
  } else if (boothId >= 700 && boothId < 1000) {
    location.world = BoothTab.WORLD3;
    calc -= BoothTabIdIncrease[BoothTab.WORLD3];
  } else {
    console.warn(`Dealer ID:${boothId} couldn't be grouped to a world.`);
  }

  // Calculate section
  if (calc >= BoothTabSectionIdIncrease[BoothTabSection.UPPER]) {
    location.section = BoothTabSection.UPPER;
  } else if (calc >= BoothTabSectionIdIncrease[BoothTabSection.MIDDLE]) {
    location.section = BoothTabSection.MIDDLE;
  } else if (calc >= BoothTabSectionIdIncrease[BoothTabSection.LOWER]) {
    location.section = BoothTabSection.LOWER;
  } else {
    console.warn(`Dealer ID:${boothId} ${calc} couldn't be grouped to a section.`);
  }

  return location;
};

type DealersViewProps = {
  dealers: DealerBoothListElementDto[];
};

const escapeRegex = (str: string) => {
  return str.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
};

export const useActiveBooth = (
  dealers: DealerBoothListElementDto[] | undefined = [],
): number | null => {
  const search = useSearch({
    from: '/explore/dealers',
  });

  return useMemo(
    () => dealers.find((dealer) => dealer.boothId === search.id)?.boothId ?? null,
    [dealers, search.id],
  );
};

type SearchbarProps = {
  className?: string;
  value: string;
  onSearch: ChangeEventHandler<HTMLInputElement>;
};
const Searchbar: FC<SearchbarProps> = ({ className, onSearch, value }) => {
  const { t } = useTranslation(['common', 'avatar_showcase', 'dealers']);
  const searchbarInputRef = useRef<HTMLInputElement>(null);

  // Debounce setting the searchbar so that it doesn't feel laggy when typing.
  useDebounceEffect(
    () => {
      if (searchbarInputRef.current) {
        searchbarInputRef.current.value = value;
      }
    },
    [value, searchbarInputRef],
    { wait: 100 },
  );

  return (
    <Form.Control
      as="input"
      type="search"
      className={clsx('', className)}
      onChange={(event: ChangeEvent<HTMLInputElement>) => onSearch(event)}
      placeholder={t('search_for_dealers_by_name_booth_')}
      ref={searchbarInputRef}
    />
  );
};

type DropdownToggleProps = {
  currentTags: string[];
} & Omit<ComponentProps<typeof Dropdown.Toggle>, 'children'>;
const DropdownToggle: FC<DropdownToggleProps> = ({ currentTags, ...toggleProps }) => {
  const { t } = useTranslation();

  return (
    <TooltipHelper
      id="active-tags"
      title={currentTags.join(', ')}
      disableTooltip={currentTags.length === 0}
      child={
        <Dropdown.Toggle {...toggleProps}>
          {currentTags.length === 0 && t('search_by_tags')}
          {currentTags.length !== 0 && t('filtered_by_tags', { tagCount: currentTags.length })}
          <FontAwesomeIcon icon={faAngleDown} style={{ marginLeft: '10px' }} />
        </Dropdown.Toggle>
      }
    />
  );
};

type DropdownMenuProps = {
  resetFilters: () => void;
  allTags: string[];
  handleTagClick: (tag: string) => void;
  currentTags: string[];
};
const DropdownMenu: FC<DropdownMenuProps> = ({
  resetFilters,
  allTags,
  handleTagClick,
  currentTags,
}) => {
  const { t } = useTranslation(['common', 'dealers']);

  return (
    <Dropdown.Menu style={{ height: '400px', overflowY: 'scroll' }}>
      <Dropdown.Item as="button" className="text-danger" onClick={resetFilters}>
        {t('reset')}
      </Dropdown.Item>
      <Dropdown.Divider />
      {allTags.map((tag) => (
        <TagItem key={tag} name={tag} onTagSelect={handleTagClick} currentTags={currentTags} />
      ))}
    </Dropdown.Menu>
  );
};

const DealersView: FC<DealersViewProps> = ({ dealers }) => {
  const dealersDenMap = useFlag('dealersDen.map');
  const dealersDenPreview = useFlag('dealersDen.preview');
  const { registered } = useIsRegistered();

  const refTop = useRef<HTMLDivElement>(null);
  const refList = useRef<HTMLDivElement>(null);
  const [locateBooth, setLocateBooth] = useState<number>(NO_BOOTH);
  const [searchText, setSearchText] = useState<string>('');
  const [isMobileMapEnabled, setMobileMapEnabled] = useState(false);
  const [bookmarks, setBookmarks] = useState<number[]>([]);
  const [currentTags, setCurrentTags] = useState<string[]>([]);
  const [currentTab, setCurrentTab] = useState<BoothTab>(BoothTab.ALL);
  const [currentTabSection, setCurrentTabSection] = useState<BoothTabSection>(BoothTabSection.ALL);

  // The query parameter is the source of truth for the active booth.
  const activeBooth = useActiveBooth(dealers);

  const tabFilter = useCallback(
    (dealer: DealerBoothListElementDto) => {
      if (currentTab === BoothTab.ALL) {
        return true;
      } else if (currentTab === BoothTab.BOOKMARKS) {
        return dealer.bookmarked;
      }
      const { world, section } = getBoothLocation(dealer.boothId);
      // No world
      return (
        world === null ||
        ([world, BoothTab.ALL].includes(currentTab) &&
          [section, BoothTabSection.ALL].includes(currentTabSection))
      );
    },
    [currentTab, currentTabSection],
  );
  // const bookmarkFilter = useCallback((dealer: DealerSummary) => dealer.bookmarked, []);
  const tagFilter = useCallback(
    (dealer: DealerBoothListElementDto) =>
      currentTags.every((currentTag) => dealer.tags.includes(currentTag)),
    [currentTags],
  );

  const tabDealers: DealerBoothListElementDto[] = useMemo(
    () => dealers.filter(tabFilter),
    [dealers, tabFilter],
  );

  const filteredDealers: DealerBoothListElementDto[] = useMemo(() => {
    let result = tabDealers;

    if (currentTags.length > 0) {
      result = result.filter(tagFilter);
    }

    if (searchText) {
      // if 3 characters and numeric, assume booth ID?
      const boothNr = searchText.length <= 3 ? Math.floor(+searchText) : undefined;
      if (boothNr !== undefined && !isNaN(boothNr)) {
        result = result.filter((dealer) => dealer.boothId === boothNr);
      } else {
        const searchTerm = new RegExp(escapeRegex(searchText), 'mi');
        result = result.filter(
          (dealer) =>
            searchTerm.test(dealer.name) ||
            searchTerm.test(dealer.shortDescription) ||
            searchTerm.test(dealer.longDescription) ||
            searchTerm.test(dealer.boothId.toString()) ||
            (dealer.tags.length > 0 && searchTerm.test(dealer.tags.toString())),
        );
      }
    }

    return result;
  }, [tabDealers, currentTags, searchText, tagFilter]);

  const isFiltered: boolean = currentTags.length > 0 || searchText.length > 0;

  const handleSearch = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      const value = event.target.value;
      setSearchText(value);
    },
    [setSearchText],
  );

  const handleTagsClick = useCallback(
    (tag: string, scroll: boolean = true) => {
      if (currentTags.includes(tag)) {
        setCurrentTags((prevArray) => prevArray.filter((currentTag) => currentTag !== tag));
      } else {
        setCurrentTags((prevArray) => [...prevArray, tag]);
      }
      if (scroll) {
        refTop.current?.scrollIntoView();
      }
    },
    [currentTags, setCurrentTags, refTop],
  );

  const handleBoothClick = (id: number) => {
    if (locateBooth === id) {
      setSearchText('');
      setLocateBooth(NO_BOOTH);
      return;
    }
    setSearchText(id.toString());
    setLocateBooth(id);
    if (isMobileMapEnabled) {
      setMobileMapEnabled(false);
    }
    refList.current?.scrollIntoView();
  };

  const handleSectionClick = (section: BoothTabSection) => {
    setSearchText('');
    setLocateBooth(NO_BOOTH);
    if (currentTabSection === section) {
      setCurrentTabSection(BoothTabSection.ALL);
    } else {
      setCurrentTabSection(section);
    }
  };

  const handleLocateBooth = (id: number) => {
    const dealer = dealers.find((d) => d.boothId === id);
    if (!dealer) {
      console.error(`Couldnt locate an unknown booth ID:${id}`);
      return;
    }

    const location = getBoothLocation(dealer.boothId);
    if (location.world === null) {
      console.error(`Couldnt find world for booth ID:${id}`);
      return;
    }
    if (location.section === null) {
      console.error(`Couldnt find section for booth ID:${id}`);
      return;
    }

    refTop.current?.scrollIntoView();
    setCurrentTab(location.world);
    setCurrentTabSection(location.section);
    setLocateBooth(id);
  };

  // Check for "?locate=id" in query string and handle locate navigation.
  const search = useSearch({ from: '/explore/dealers' });

  useEffect(() => {
    if (locateBooth === NO_BOOTH) {
      if (search.locate != null && search.locate !== NO_BOOTH) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        handleLocateBooth(search.locate);
      }
    }
  }, [location.search]);

  const handleTabClick = (tab: BoothTab) => {
    setCurrentTab(tab);
    setCurrentTabSection(BoothTabSection.ALL);
    setSearchText('');
    setLocateBooth(NO_BOOTH);
  };

  // poor mans redux updater for dealers
  const updateDealersItem = (boothIndex: number, bookmarked: boolean) => {
    if (bookmarked) {
      if (bookmarks.includes(boothIndex)) {
        setBookmarks([...bookmarks.filter((bookmark) => bookmark !== boothIndex)]);
      }
    } else if (!bookmarks.includes(boothIndex)) {
      setBookmarks([...bookmarks, boothIndex]);
    }
  };

  const resetFilters = () => {
    setSearchText('');
    setCurrentTags([]);
    setLocateBooth(NO_BOOTH);
  };

  const allTags: string[] = useMemo(() => {
    const tags = new Set<string>();
    dealers.forEach((dealer) => {
      dealer.tags.forEach((tag) => {
        tags.add(tag);
      });
    });

    return Array.from(tags).sort();
  }, [dealers]);

  const { t } = useTranslation(['common', 'dealers']);

  if (activeBooth !== null) {
    return <Booth activeBoothId={activeBooth} />;
  }

  const sectionKey = sectionI18nKey(currentTabSection);

  const worldTabs: BoothTab[] = [BoothTab.WORLD1, BoothTab.WORLD2, BoothTab.WORLD3];

  return (
    <div ref={refTop} className="animate__animated animate__fadeIn animate__faster">
      {registered && !dealersDenPreview && <JoinDealersWorldButton eventId="dealers-den" />}
      <nav className="d-flex flex-wrap justify-content-center column-gap-3 mb-3 flex-shrink-1">
        <div className="input-group-merge col-12 col-lg-auto">
          <TooltipHelper
            id="tab-all"
            title={t('dealers:tab.tooltip', { tab: t(tabI18nKey(BoothTab.ALL)) })}
            child={
              <Button
                className={clsx(
                  'input-group-text text-white w-100',
                  currentTab === BoothTab.ALL && 'bg-white text-dark',
                )}
                onClick={() => handleTabClick(BoothTab.ALL)}
              >
                <FontAwesomeIcon icon={faInfinity} />
                <span>{t(tabI18nKey(BoothTab.ALL))}</span>
              </Button>
            }
          />
        </div>
        <div className="input-group-merge d-flex flex-wrap column-gap-1 col-12 col-lg-auto justify-content-center">
          {[BoothTab.WORLD1, BoothTab.WORLD2, BoothTab.WORLD3].map((worldTab) => (
            <TooltipHelper
              key={`tab-${worldTab}`}
              id={`tab-${worldTab}`}
              title={t('dealers:tab.tooltip', { tab: t(tabI18nKey(worldTab)) })}
              child={
                <Button
                  className={clsx(
                    'input-group-text text-white mr-0 col-12 col-lg-auto',
                    currentTab === worldTab && 'bg-white text-dark',
                  )}
                  onClick={() => handleTabClick(worldTab)}
                >
                  <FontAwesomeIcon icon={faGlobe} />
                  <span>{t(tabI18nKey(worldTab))}</span>
                </Button>
              }
            />
          ))}
        </div>
        <div className="input-group-merge col-12 col-lg-auto">
          <TooltipHelper
            id="filter-by-bookmarks"
            title={t('dealers:tab.tooltip', { tab: t(tabI18nKey(BoothTab.BOOKMARKS)) })}
            child={
              <Button
                className={clsx(
                  'input-group-text text-white w-100',
                  currentTab === BoothTab.BOOKMARKS && 'bg-warning text-dark',
                )}
                onClick={() => handleTabClick(BoothTab.BOOKMARKS)}
              >
                <FontAwesomeIcon className="mr-lg-0" icon={faBookmark} />
                <span className="d-lg-none">{t(tabI18nKey(BoothTab.BOOKMARKS))}</span>
              </Button>
            }
          />
        </div>
      </nav>

      {dealersDenMap && worldTabs.includes(currentTab) && (
        <div className="mb-3">
          <Map
            onBoothSelect={(boothId) =>
              handleBoothClick(boothIdFromIndex(boothId, currentTab, currentTabSection))
            }
            onSectionSelect={(tabSection) => handleSectionClick(tabSection)}
            highlightedBoothIndex={boothIndexFromId(locateBooth)}
            dealers={dealers}
            currentMap={currentTab}
            currentMapSection={currentTabSection}
          />
        </div>
      )}
      <div ref={refList} className="mb-2">
        <search className="d-lg-block d-md-none d-sm-none d-none">
          <div className="sticky-top" style={{ zIndex: '100' }}>
            <InputGroup
              // TODO: refactor with react-bootstrap components
              className="input-group-merge"
            >
              <InputGroup.Text>
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </InputGroup.Text>
              <Searchbar value={searchText} onSearch={handleSearch} />
              <Dropdown className={searchbarStyle['searchDropdown']}>
                <DropdownToggle
                  variant="outline-secondary"
                  className="input-group-text text-white mr-0"
                  currentTags={currentTags}
                />
                <DropdownMenu
                  resetFilters={resetFilters}
                  handleTagClick={(tag: string) => handleTagsClick(tag, false)}
                  currentTags={currentTags}
                  allTags={allTags}
                />
              </Dropdown>
              {isFiltered && (
                <TooltipHelper
                  id="reset-filters"
                  title={t('reset_filters')}
                  child={
                    <Button variant="danger" className="text-white mr-0" onClick={resetFilters}>
                      <FontAwesomeIcon icon={faFilterSlash} />
                    </Button>
                  }
                />
              )}
            </InputGroup>
          </div>
        </search>
        <div className="d-lg-none d-md-block d-sm-block text-center">
          <Searchbar value={searchText} onSearch={handleSearch} className="mb-2" />
          <Dropdown className="mb-2 w-100">
            <DropdownToggle
              variant="dark"
              className="text-white w-100"
              size="sm"
              currentTags={currentTags}
            />

            <DropdownMenu
              resetFilters={resetFilters}
              handleTagClick={handleTagsClick}
              currentTags={currentTags}
              allTags={allTags}
            />
          </Dropdown>
          {isFiltered && (
            <TooltipHelper
              id="reset-filters"
              title={t('reset_filters')}
              child={
                <Button
                  size="sm"
                  variant="danger"
                  icon={<FontAwesomeIcon icon={faFilterSlash} />}
                  onClick={resetFilters}
                >
                  {t('reset_filters')}
                </Button>
              }
            />
          )}
        </div>
      </div>

      <div className="mb-3 text-center fst-italic text-muted">
        <Trans
          ns="dealers"
          i18nKey="search.results.summary"
          components={{
            highlight: (
              <span
                className={clsx({
                  'text-warning': filteredDealers.length < tabDealers.length,
                  'text-danger': !filteredDealers.length,
                })}
              />
            ),
            p: <span className="text-white" />,
          }}
          values={{
            results: filteredDealers.length,
            total: tabDealers.length,
            tab: t(tabI18nKey(currentTab)),
            section: sectionKey ? t(sectionKey) : null,
          }}
        />
      </div>

      {/*isMobileMapEnabled && (
        //camping the i18n key until we add it back: dealers:mobile.view_map
          <>
            <div
            // When Mobile map is showing, cover up the rest of the dealer page else with this dark blue div
              className="fixed-top bg-dark d-lg-none"
              style={{ zIndex: '100', overflow: 'none', height: '100%', width: '100%' }}
            >
              {dealers.isSuccess && (
                <Map
                  handleBoothClick={handleMapClick}
                  highlightedBooth={locateBooth}
                  dealers={dealers.data.data}
                  isMobileMapEnabled={isMobileMapEnabled}
                  onCloseButtonClick={() => setMobileMapEnabled(false)}
                />
              )}
            </div>
          </>
        )*/}
      <Row>
        {filteredDealers.map((booth, index) => (
          <Col
            xs={12}
            md={6}
            lg={4}
            className="mb-4"
            key={booth.boothId}
            style={{
              minHeight: 286.75,
            }}
          >
            <BoothCard
              booth={booth}
              index={index}
              handleTagsClick={handleTagsClick}
              setBoothBookmark={updateDealersItem}
              setLocateBooth={handleLocateBooth}
            />
          </Col>
        ))}
      </Row>
      {filteredDealers.length === 0 && (
        <div className="fs-4 my-4 text-center text-muted">
          <p>{t('dealers:no_results')}</p>
        </div>
      )}
    </div>
  );
};

export default DealersView;
